import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { cx } from "../../Util/classHelpers";
import { useFetchChDrilldownQuery, useLazyFetchChDrilldownQuery } from "../../Apis/chAnalyticsApi";
import { METRIC_CONFIG, TIMEFRAME_OPTIONS } from "./chMetricConfig";
import { ChLocationIcon, ChCalendarIcon, DownloadIcon } from "../Icons/ch-analytics-icons";
import OrderDetailModal from "../Orders/OrderDetailModal";
import { exportToCsv } from "../../Util/csvExport";
import { useOnTimeBufferMinutes } from "../../Hooks/useOnTimeBufferMinutes";
import styles from "./chDrawer.module.css";

/**
 * Outer shell — always mounted, handles overlay + slide animation.
 * Inner content is keyed by companyId+metric+timeframe so React fully
 * unmounts the old tree (destroying the RTK Query subscription) and mounts
 * a fresh one whenever the drilldown target changes. This guarantees
 * zero stale data between drilldowns.
 */
const ChDrilldownDrawer = ({ drawerState, timeframe, startDate, endDate, dateRange, onClose, onPageChange }) => {
    const { isOpen, companyId, companyName, metric, page } = drawerState;

    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape" && isOpen) onClose();
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    const contentKey = `${companyId}-${metric}-${timeframe}`;

    return ReactDOM.createPortal(
        <>
            <div
                className={cx(styles.overlay, isOpen && styles.overlayShow)}
                onClick={onClose}
            />
            <aside className={cx(styles.drawer, isOpen && styles.drawerOpen)}>
                {isOpen && companyId && metric && (
                    <DrilldownContent
                        key={contentKey}
                        companyId={companyId}
                        companyName={companyName}
                        metric={metric}
                        timeframe={timeframe}
                        startDate={startDate}
                        endDate={endDate}
                        dateRange={dateRange}
                        page={page}
                        onClose={onClose}
                        onPageChange={onPageChange}
                    />
                )}
            </aside>
        </>,
        document.body
    );
};

/**
 * Inner content — owns the RTK Query subscription.
 * Keyed by parent so it fully remounts on every new drilldown,
 * guaranteeing a fresh fetch with no stale data.
 */
const DrilldownContent = ({ companyId, companyName, metric, timeframe, startDate, endDate, dateRange, page, onClose, onPageChange }) => {
    const pageSize = 25;
    const cfg = METRIC_CONFIG[metric];
    const tfLabel = timeframe === "range" && dateRange?.start && dateRange?.end
        ? `${fmtRangeDate(dateRange.start)} – ${fmtRangeDate(dateRange.end)}`
        : TIMEFRAME_OPTIONS.find((t) => t.value === timeframe)?.label ?? timeframe;

    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [truncatedWarning, setTruncatedWarning] = useState(false);

    const onTimeBufferMinutes = useOnTimeBufferMinutes();

    const handleOrderClick = useCallback((orderId) => {
        setSelectedOrderId(orderId);
    }, []);

    const handleOrderModalClose = useCallback(() => {
        setSelectedOrderId(null);
    }, []);

    const { data, isFetching } = useFetchChDrilldownQuery(
        { companyId, metric, timeframe, startDate, endDate, page, pageSize, onTimeBufferMinutes }
    );

    const [triggerExport] = useLazyFetchChDrilldownQuery();

    const handleExport = useCallback(async () => {
        setIsExporting(true);
        setTruncatedWarning(false);
        try {
            const today = new Date().toISOString().slice(0, 10);
            const filename = `${metric.toLowerCase().replace(/_/g, "-")}_${timeframe}_${today}.csv`;
            const result = await triggerExport({ companyId, metric, timeframe, startDate, endDate, exportAll: true, onTimeBufferMinutes }).unwrap();
            exportToCsv(cfg.columns, result.orders, filename, { onTimeBufferMinutes });
            if (result.truncated) setTruncatedWarning(true);
        } finally {
            setIsExporting(false);
        }
    }, [triggerExport, companyId, metric, timeframe, startDate, endDate, cfg, onTimeBufferMinutes]);

    const orders = data?.orders ?? [];
    const stats = data?.stats ?? null;
    const totalPages = data?.totalPages ?? 0;
    const totalCount = stats?.totalCount ?? 0;
    const statItems = stats && cfg ? cfg.stats(stats) : null;

    if (!cfg) return null;

    return (
        <>
            <DrawerHeader
                cfg={cfg}
                companyName={companyName}
                tfLabel={tfLabel}
                statItems={statItems}
                isExporting={isExporting}
                onExport={handleExport}
                onClose={onClose}
            />
            {truncatedWarning && (
                <div className={styles.truncatedBanner}>
                    {"Export limited to 10,000 rows. Refine your filters to get complete data."}
                    <button className={styles.truncatedDismiss} onClick={() => setTruncatedWarning(false)}>{"✕"}</button>
                </div>
            )}
            <div className={styles.body}>
                <DrawerTable
                    cfg={cfg}
                    orders={orders}
                    loading={isFetching}
                    onOrderClick={handleOrderClick}
                />
            </div>
            <div className={styles.footer}>
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    pageSize={pageSize}
                    loading={isFetching}
                    onPageChange={onPageChange}
                />
            </div>
            {ReactDOM.createPortal(
                <OrderDetailModal
                    orderId={selectedOrderId}
                    companyId={companyId}
                    open={!!selectedOrderId}
                    onClose={handleOrderModalClose}
                />,
                document.body
            )}
        </>
    );
};

const DrawerHeader = ({ cfg, companyName, tfLabel, statItems, isExporting, onExport, onClose }) => {
    return (
        <div className={styles.header}>
            <div className={styles.context}>
                <span>Analytics</span>
                <span className={styles.ctxSep}>{"\u203A"}</span>
                <span className={styles.ctxActive}>{cfg.label}</span>
            </div>

            <div className={styles.titleRow}>
                <div className={styles.iconWrap} style={{ background: cfg.paleBg, color: cfg.color }}>
                    {cfg.icon}
                </div>
                <div className={styles.titleGroup}>
                    <div className={styles.title}>
                        {cfg.label}
                    </div>
                </div>
                <button
                    className={styles.exportBtn}
                    onClick={onExport}
                    disabled={isExporting}
                    title="Export as CSV"
                >
                    <DownloadIcon size={13} />
                    {isExporting ? "Exporting\u2026" : "Export"}
                </button>
                <button className={styles.closeBtn} onClick={onClose}>{"\u2715"}</button>
            </div>

            <div className={styles.scope}>
                <span className={styles.scopeTag}>
                    <ChLocationIcon size={11} />
                    {companyName}
                </span>
                <span className={styles.scopeTag}>
                    <ChCalendarIcon />
                    {tfLabel}
                </span>
            </div>

            {statItems && (
                <div className={styles.stats}>
                    {statItems.map(({ value, label }) => (
                        <div key={label} className={styles.stat}>
                            <div className={styles.statVal}>{value ?? "\u2014"}</div>
                            <div className={styles.statLbl}>{label}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const getCellTitle = (col, order) => {
    if (col.type === "orderlink") return undefined;
    if (col.type === "ontime") {
        const promised = order.promisedDeliveryTime;
        const delivered = order.deliveryTime;
        if (promised && delivered) {
            return `Promised: ${formatDatetime(promised)} · Delivered: ${formatDatetime(delivered)}`;
        }
        return undefined;
    }
    return String(order[col.key] ?? "");
};

const DrawerTable = ({ cfg, orders, loading, onOrderClick }) => {
    return (
        <table className={styles.table}>
            <thead>
                <tr>
                    {cfg.columns.map((col) => (
                        <th key={col.key}>{col.header}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>
                            {cfg.columns.map((col) => (
                                <td key={col.key}><div className={styles.skelRow} /></td>
                            ))}
                        </tr>
                    ))
                ) : orders.length === 0 ? (
                    <tr>
                        <td colSpan={cfg.columns.length} className={styles.empty}>No orders found</td>
                    </tr>
                ) : (
                    orders.map((order, i) => (
                        <tr key={order.orderId ?? order.orderNumber ?? i}>
                            {cfg.columns.map((col) => (
                                <td key={col.key} title={getCellTitle(col, order)}>
                                    <CellValue col={col} order={order} onOrderClick={onOrderClick} />
                                </td>
                            ))}
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    );
};

const CellValue = ({ col, order, onOrderClick }) => {
    const raw = order[col.key];
    const onTimeBufferMins = useOnTimeBufferMinutes();

    switch (col.type) {
        case "orderlink": {
            const isDeleted = order.orderStatus?.toLowerCase() === "deleted";
            if (isDeleted) {
                return <span>{raw || "—"}</span>;
            }
            return (
                <button
                    className={styles.orderLink}
                    onClick={() => onOrderClick(order.orderId)}
                >
                    {raw || "—"}
                </button>
            );
        }

        case "datetime":
            return <span className={styles.dtVal}>{formatDatetime(raw)}</span>;

        case "badge":
            return <StatusBadge value={raw} />;

        case "ontime": {
            const promised = order.promisedDeliveryTime;
            const delivered = order.deliveryTime;

            if (promised && delivered) {
                // Truncate toward zero to match the backend's integer-minute
                // lateness ((to - from) / 60000 via integer division), so the
                // badge always agrees with the backend's on-time classification.
                const diffMins = Math.trunc(
                    (new Date(delivered.replace(" ", "T")) - new Date(promised.replace(" ", "T"))) / 60000
                );
                // diffMins > 0 => late, < 0 => early, 0 => exactly on time.
                if (diffMins < 0) {
                    return <span className={cx(styles.badge, styles.badgeOntime)}>{Math.abs(diffMins)} min early</span>;
                }
                if (diffMins <= onTimeBufferMins) {
                    // On time, or late but within the grace buffer.
                    return <span className={cx(styles.badge, styles.badgeOntime)}>On-time</span>;
                }
                return <span className={cx(styles.badge, styles.badgeLate)}>{diffMins} min late</span>;
            }

            // On-time is undefined without both a promise and a delivery time
            // (matches the backend, which leaves lateness NULL and excludes such
            // orders from every metric). Show neutral "—", never classify.
            return <span>—</span>;
        }

        case "waiting": {
            const mins = Number(raw);
            const cls = mins > 180 ? styles.ageCrit : mins > 60 ? styles.ageWarn : styles.ageFresh;
            return <span className={cls}>{mins} min</span>;
        }

        default:
            return <span>{raw || "\u2014"}</span>;
    }
};

const StatusBadge = ({ value }) => {
    if (!value) return <span>{"\u2014"}</span>;

    const v = String(value).toLowerCase();
    const map = {
        delivered: styles.badgeDelivered,
        driver: styles.badgeDriver,
        dispatcher: styles.badgeDispatcher,
        deleted: styles.badgeDeleted,
        failed: styles.badgeFailed,
        incomplete: styles.badgeIncomplete,
        not_assigned: styles.badgeUnassigned,
        assigned: styles.badgeAssigned,
        dispatcher_delivery: styles.badgeDispatcher,
        driver_delivery: styles.badgeDriver,
        already_delivered: styles.badgeDelivered,
    };

    let cls = map[v];
    if (!cls) {
        if (v.includes("driver")) cls = styles.badgeDriver;
        else if (v.includes("disp")) cls = styles.badgeDispatcher;
        else if (v.includes("deliver")) cls = styles.badgeDelivered;
        else if (v.includes("delete")) cls = styles.badgeDeleted;
        else cls = styles.badgeDefault;
    }

    return <span className={cx(styles.badge, cls)}>{value}</span>;
};

const Pagination = ({ page, totalPages, totalCount, pageSize, loading, onPageChange }) => {
    if (!totalPages) return null;

    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, totalCount);
    const pages = buildPageRange(page, totalPages);

    return (
        <div className={styles.pagination}>
            <span className={styles.pgInfo}>
                {loading ? "Loading\u2026" : `Showing ${from}\u2013${to} of ${totalCount.toLocaleString()}`}
            </span>
            <div className={styles.pgBtns}>
                <button
                    className={styles.pgBtn}
                    disabled={page <= 1 || loading}
                    onClick={() => onPageChange(page - 1)}
                >
                    {"\u2039"}
                </button>
                {pages.map((p, i) =>
                    p === "\u2026" ? (
                        <span key={`ellipsis-${i}`} className={styles.pgEllipsis}>{"\u2026"}</span>
                    ) : (
                        <button
                            key={p}
                            className={cx(styles.pgBtn, p === page && styles.pgCur)}
                            disabled={loading}
                            onClick={() => p !== page && onPageChange(p)}
                        >
                            {p}
                        </button>
                    )
                )}
                <button
                    className={styles.pgBtn}
                    disabled={page >= totalPages || loading}
                    onClick={() => onPageChange(page + 1)}
                >
                    {"\u203A"}
                </button>
            </div>
        </div>
    );
};

function buildPageRange(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, "\u2026", total];
    if (current >= total - 3) return [1, "\u2026", total - 4, total - 3, total - 2, total - 1, total];
    return [1, "\u2026", current - 1, current, current + 1, "\u2026", total];
}

function fmtRangeDate(dateStr) {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
    });
}

function formatDatetime(val) {
    if (!val) return "\u2014";
    try {
        const d = new Date(val.replace(" ", "T"));
        if (isNaN(d)) return val;
        return d.toLocaleString("en-US", {
            month: "short", day: "numeric",
            hour: "numeric", minute: "2-digit", hour12: true,
        });
    } catch {
        return val;
    }
}

export default ChDrilldownDrawer;
